import type { EditorOption, EditorOptionNode } from './types'
import { normalizeCreateLabel } from './utils'

export type GroupCandidate = {
  id: string
  label: string
  pathLabel: string
}

export const hasGroupedEditorOptions = (
  options: EditorOptionNode[] | undefined,
): boolean => {
  if (!Array.isArray(options) || options.length === 0) return false

  const stack = [...options]
  while (stack.length > 0) {
    const node = stack.pop()
    if (!node) continue

    if (Array.isArray(node.options) && node.options.length > 0) {
      return true
    }

    if (Array.isArray(node.options)) {
      stack.push(...node.options)
    }
  }

  return false
}

export const extractGroupCandidates = (
  options: EditorOptionNode[] | undefined,
  ancestors: string[] = [],
): GroupCandidate[] => {
  if (!Array.isArray(options) || options.length === 0) return []

  return options.flatMap((node) => {
    if (!Array.isArray(node.options) || node.options.length === 0) {
      return []
    }

    const nextAncestors = [...ancestors, node.label].filter(Boolean)
    return [
      {
        id: node.id,
        label: node.label,
        pathLabel: nextAncestors.join(' > '),
      },
      ...extractGroupCandidates(node.options, nextAncestors),
    ]
  })
}

export const findGroupNodeById = (
  options: EditorOptionNode[] | undefined,
  groupId: string,
): EditorOptionNode | undefined => {
  if (!Array.isArray(options) || options.length === 0) return undefined

  const stack = [...options]
  while (stack.length > 0) {
    const node = stack.pop()
    if (!node) continue

    if (node.id === groupId) return node

    if (Array.isArray(node.options) && node.options.length > 0) {
      stack.push(...node.options)
    }
  }

  return undefined
}

export const findExistingEditorOptionInGroup = ({
  options,
  inputValue,
  groupId,
}: {
  options: EditorOptionNode[] | undefined
  inputValue: string
  groupId: string
}): EditorOption | undefined => {
  const normalizedInput = normalizeCreateLabel(inputValue)
  if (!normalizedInput) return undefined

  const parentGroup = findGroupNodeById(options, groupId)
  if (!parentGroup?.options?.length) return undefined

  return parentGroup.options.find(
    (option) =>
      (!option.options || option.options.length === 0) &&
      normalizeCreateLabel(option.label) === normalizedInput,
  )
}
